// components/portal/ContactOrgFolders.tsx
// Contacts page as a folder browser: Organisation (tenant/brand) → that tenant's contacts. Contacts
// hang off a brand via Contact.parentId; folder counts come from /api/inventory/contacts/org-buckets
// and the leaf renders the shared ContactsTable scoped to the brand.
'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Loader2 } from '@/components/ui/lucide-memo';
import { useKernelStore } from '@/kernel/kernelStores';
import { ContactsTable } from '@/components/entities/tables/contacts/ContactsTable';
import { Breadcrumb, FolderGrid, FolderCard, FolderSearch } from './folderBrowserUI';

interface OrgBucket { brandId: string; name: string; logoUrl: string | null; count: number }

// Memoized so that typing in the search box only re-renders the folders that enter or leave the
// filtered set, instead of rebuilding every card of a list that is expected to be long.
const OrgFolderCard = React.memo(({ org, fr, onSelect }: { org: OrgBucket; fr: boolean; onSelect: (org: OrgBucket) => void }) => {
  const handleClick = useCallback(() => onSelect(org), [onSelect, org]);
  return (
    <FolderCard
      icon={
        org.logoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={org.logoUrl} alt="" className="h-full w-full rounded-md bg-white object-contain p-0.5" />
        ) : (
          <Building2 size={20} className="text-[#7A7A7A] transition-colors group-hover:text-[#9A9A9A]" />
        )
      }
      title={org.name}
      subtitle={`${org.count.toLocaleString(fr ? 'fr-FR' : 'en-US')} ${fr ? 'contacts' : 'contacts'}`}
      onClick={handleClick}
    />
  );
});
OrgFolderCard.displayName = 'OrgFolderCard';

export const ContactOrgFolders = () => {
  const fr = useKernelStore((s) => s.language) === 'fr';
  const [data, setData] = useState<{ organizations: OrgBucket[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const [org, setOrg] = useState<OrgBucket | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/inventory/contacts/org-buckets')
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((j) => { if (!cancelled) setData(j); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const orgs = data?.organizations ?? [];
    const q = query.trim().toLowerCase();
    return q ? orgs.filter((o) => o.name.toLowerCase().includes(q)) : orgs;
  }, [data, query]);

  const handleBack = useCallback(() => setOrg(null), []);
  const handleSelect = useCallback((next: OrgBucket) => setOrg(next), []);
  const leafCrumbs = useMemo(
    () => [{ label: fr ? 'Contacts' : 'Contacts', onClick: handleBack }, { label: org?.name ?? '' }],
    [fr, org, handleBack],
  );
  const rootCrumbs = useMemo(() => [{ label: fr ? 'Contacts par organisation' : 'Contacts by organization' }], [fr]);

  // Leaf: the brand's contacts
  if (org) {
    return (
      <div className="flex h-full w-full flex-col">
        <Breadcrumb items={leafCrumbs} />
        <div className="min-h-0 flex-1">
          <ContactsTable key={org.brandId} brandId={org.brandId} />
        </div>
      </div>
    );
  }

  if (!data && !failed) {
    return (
      <div className="flex h-full w-full items-center justify-center gap-2 text-[13px] text-[#8A8A8A]">
        <Loader2 size={16} className="animate-spin" /> {fr ? 'Chargement des dossiers…' : 'Loading folders…'}
      </div>
    );
  }
  if (failed || !data) {
    return <div className="flex h-full w-full items-center justify-center text-[13px] text-[#8A8A8A]">{fr ? 'Indisponible.' : 'Unavailable.'}</div>;
  }

  // Root → one folder per organisation (tenant). Searchable — there can be many.
  return (
    <div className="flex h-full w-full flex-col">
      <Breadcrumb items={rootCrumbs} />
      <FolderSearch
        value={query}
        onChange={setQuery}
        placeholder={fr ? 'Rechercher une enseigne…' : 'Search a tenant…'}
      />
      <FolderGrid>
        {filtered.map((o) => (
          <OrgFolderCard key={o.brandId} org={o} fr={fr} onSelect={handleSelect} />
        ))}
      </FolderGrid>
    </div>
  );
};

ContactOrgFolders.displayName = 'ContactOrgFolders';
